import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Container, Title, Copy, IconWrap, IconWrapper } from './InfoPopover.styles';
import { Icon, Popover } from '@oneaudi/unified-web-components';

interface InfoPopoverProps {
  id: string;
  title?: string;
  description: string;
  iconSize?: 'xs' | 's' | 'l';
  align?: 'left' | 'right' | 'center';
  iconColor?: string;
  bgColor?: string;
  triggerTestId?: string;
  iconElevated?: boolean;
  isVehicleCardComponent?: boolean;
}

const InfoPopover: React.FC<InfoPopoverProps> = ({
  id,
  title,
  description,
  align = 'center',
  iconColor = '',
  triggerTestId,
  iconElevated,
  iconSize = 'xs',
}) => {
  const { t } = useTranslation();

  const [showInfoPopover, setShowInfoPopover] = useState(false);
  const defaultContainerPosition = 2000;
  const closePopover = useCallback(() => {
    setShowInfoPopover(false);
  }, []);

  const togglePopover = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setShowInfoPopover((value) => !value);
  }, []);

  useEffect(() => {
    if (!showInfoPopover)
      return () => {
        // noop
      };

    document.addEventListener('click', closePopover);

    return () => {
      document.removeEventListener('click', closePopover);
    };
  }, [closePopover, showInfoPopover]);

  return (
    <Popover
      id={id}
      open={showInfoPopover}
      placement="bottom"
      onClose={closePopover}
      triggerElement={
        <IconWrap
          onClick={togglePopover}
          aria-label={t('vlp.vehicleResults.popoverInformation')}
          data-testid={triggerTestId}
          iconElevated={iconElevated}
        >
          <IconWrapper color={iconColor}>
            <Icon name="info-circle" size={iconSize} />
          </IconWrapper>
        </IconWrap>
      }
      alignment={align}
      zIndex={defaultContainerPosition}
      content={
        //TODO integrate customized bgColor on unified web <Popover>
        <Container data-testid="info-popover-container">
          {title && <Title>{title}</Title>}
          <Copy>
            <Trans
              i18nKey={description}
              // the global reset css imported in several feature apps
              // contains globally applied oppinionated styling
              components={{ italic: <span style={{ fontStyle: 'italic' }} /> }}
            />
          </Copy>
        </Container>
      }
    />
  );
};

export default InfoPopover;
